import { useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Coffee, ArrowRight, ArrowLeft, Mail } from "lucide-react";

export default function AdminForgotPassword() {
    const [email, setEmail] = useState("");
    const [loading, setLoading] = useState(false);
    const [sent, setSent] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        if (!email) return;
        setLoading(true);
        try {
            await api.post("/auth/forgot-password", { email });
            setSent(true);
        } catch {
            toast.error("Something went wrong. Try again in a minute.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex bg-cream-light" data-testid="admin-forgot-password">
            {/* Left brand panel */}
            <div className="hidden md:flex md:w-1/2 text-cream p-12 flex-col justify-between relative overflow-hidden" style={{ backgroundColor: "#1a0f00" }}>
                <div className="grain-light" />
                <div className="relative z-10">
                    <div className="tracked text-gold text-[10px]">CAFE HIDEOUT · CMS</div>
                </div>
                <div className="relative z-10">
                    <Coffee size={48} strokeWidth={1.1} className="text-gold mb-6" />
                    <h1 className="font-editorial font-bold text-cream text-5xl lg:text-6xl leading-[0.95]">
                        Forgot a key? <br /> We'll cut a new one.
                    </h1>
                    <p className="font-body text-cream/70 mt-6 max-w-md text-base">
                        Send a reset link to the email on file. The link lives for 30 minutes — long enough to refill your cup.
                    </p>
                </div>
                <div className="relative z-10 font-mono text-[10px] text-cream/40">
                    © {new Date().getFullYear()} CAFE HIDEOUT
                </div>
            </div>

            {/* Right panel */}
            <div className="flex-1 flex items-center justify-center p-8 md:p-12">
                <div className="w-full max-w-md">
                    {!sent ? (
                        <form onSubmit={submit} data-testid="forgot-form">
                            <div className="tracked text-coffee/50 text-[10px] mb-4">Forgot Password</div>
                            <h2 className="font-editorial font-bold text-coffee text-4xl md:text-5xl leading-tight">
                                Reset, please.
                            </h2>
                            <p className="font-body text-coffee/60 mt-3 mb-10">
                                Enter your admin email. We'll send a one-time reset link.
                            </p>

                            <label className="tracked text-coffee/60 text-[10px] block mb-3">Email</label>
                            <input
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                data-testid="forgot-email"
                                className="w-full bg-transparent border-b border-coffee/30 focus:border-coffee py-3 text-coffee font-body outline-none"
                                required
                            />

                            <button
                                type="submit"
                                disabled={loading}
                                className="btn-pill btn-pill-coffee mt-10 w-full justify-center disabled:opacity-50"
                                data-testid="forgot-submit"
                            >
                                {loading ? "Sending…" : "Send Reset Link"} <ArrowRight size={14} />
                            </button>

                            <Link to="/admin/login" className="mt-8 inline-flex items-center gap-2 tracked text-[10px] text-coffee/60 hover:text-gold transition-colors" data-testid="back-to-login">
                                <ArrowLeft size={12} /> Back to Sign In
                            </Link>
                        </form>
                    ) : (
                        <div data-testid="forgot-sent">
                            <Mail size={32} strokeWidth={1.2} className="text-gold mb-6" />
                            <div className="tracked text-coffee/50 text-[10px] mb-4">Reset Link Sent</div>
                            <h2 className="font-editorial font-bold text-coffee text-4xl md:text-5xl leading-tight">
                                Check your inbox.
                            </h2>

                            <div className="mt-8 border-l-2 border-gold pl-5 py-3 bg-cream/40">
                                <div className="tracked text-coffee/50 text-[10px] mb-1.5">Sent To</div>
                                <div
                                    className="font-editorial font-medium text-coffee text-xl md:text-2xl break-all"
                                    data-testid="forgot-sent-email"
                                >
                                    {email}
                                </div>
                            </div>

                            <p className="font-body text-coffee/70 mt-6 leading-relaxed">
                                The reset link is valid for the next <strong className="text-coffee">30 minutes</strong> and can be used once.
                            </p>
                            <p className="font-body text-coffee/50 text-sm mt-3 leading-relaxed">
                                Not seeing it? Check spam, or wait a minute — delivery can take a moment.
                            </p>
                            <Link to="/admin/login" className="mt-10 inline-flex items-center gap-2 btn-pill btn-pill-coffee" data-testid="forgot-back-login">
                                <ArrowLeft size={12} /> Back to Sign In
                            </Link>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
